import {Form, Formik, FormikProps} from 'formik';
import {filter, forEach, isEmpty, map, mapKeys, omitBy, replace} from 'lodash';
import {useCallback, useContext, useMemo, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi, userApi} from 'api';
import {ConfirmModal, EditableSessionParticipants, PredefinedNotifierType} from 'components';
import {UserContext} from 'context';
import {useApiData, usePrevious, useUserSession} from 'hooks';
import {AssignmentInfo} from 'interfaces';
import {AnyParticipant} from 'interfaces/session';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, ParticipantRole, UserSummary} from 'types';
import {apiUtils, generateUtils, sessionUtils, userInfoUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;

type Values = {
    participants: AnyParticipant[];
}

type FormTemplateProps = {
    open: boolean;
    title: string;
    loading: boolean;
    error: ApiError;
    assignment: Partial<AssignmentInfo>;
    guestEnabled: boolean;
    assistantEnabled: boolean;
    maxGuests: number;
    maxAssistants: number;
    maxParticipants: number;
    users: UserSummary[];
    onClose: (confirm: boolean, data?: {participant?: AnyParticipant}[]) => void;
} & FormikProps<Values>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    open, title, assignment, values, submitForm, setFieldValue, error, maxParticipants,
    onClose, guestEnabled, assistantEnabled, maxGuests, maxAssistants, users, resetForm, setFieldTouched, setFieldError
}) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);
    const onModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            submitForm();
        } else {
            onClose(false);
        }
    }, [submitForm, onClose]);

    const handleAddParticipant = useCallback((role: ParticipantRole) => {
        setFieldValue(`participants[${values.participants.length}]`, {
            role,
            ...(role === ParticipantRole.Guest && {controlOnlyInfo: {}, data: {userInfo: {id: generateUtils.generateClientId()}}})
        });
    }, [setFieldValue, values.participants]);

    const handleRemoveParticipant = useCallback((index: number) => {
        const otherParticipants = filter(values.participants, (_, i) => index !== i);
        setFieldValue('participants', otherParticipants);
    }, [setFieldValue, values.participants]);

    const userInfoErrors = useMemo(() =>
        userInfoUtils.getUserInfoErrors(error, t),
    [error, t]);

    const formErrors = useMemo(() => {
        const fieldsToValidate = [];

        forEach(values.participants, (p, i) => {
            if (sessionUtils.isUserParticipant(p)) {
                fieldsToValidate.push(`participants.${i}.data.eid`);
            } else if (sessionUtils.isInviteeParticipant(p)) {
                fieldsToValidate.push(`participants.${i}.data.userInfo.id`);
            }
        });

        const result = apiUtils.getFieldsValidationErrorMessages(
            error, t, map(fieldsToValidate, (field) => ({field: `${field}`})));

        return mapKeys(omitBy({...result, ...userInfoErrors}, isEmpty), (value, key) => replace(key, '', ''));
    }, [error, t, userInfoErrors, values.participants]);

    useEffect(() => {
        forEach(formErrors, (value, key) => {
            setFieldTouched(key);
            setTimeout(() => {
                setFieldError(key, value);
            }, 0);
        });
    }, [t, setFieldError, setFieldTouched, error, formErrors]);

    useEffect(() => {
        if (!open && prevOpen) {
            resetForm();
        }
    }, [open, prevOpen, resetForm]);

    return (
        <ConfirmModal
            title={title}
            open={open}
            okDisabled={isEmpty(values.participants)}
            fullWidth
            maxWidth='sm'
            onClose={onModalClose}
        >
            <Form>
                <EditableSessionParticipants
                    participants={values.participants}
                    maxParticipants={maxParticipants}
                    assignment={assignment}
                    users={users}
                    maxParticipantsByRole={{
                        [ParticipantRole.Guest]: maxGuests,
                        [ParticipantRole.Assistant]: maxAssistants,
                    }}
                    assistantEnabled={assistantEnabled}
                    guestEnabled={guestEnabled}
                    hideDisabledRoles
                    prefillSingleParticipant
                    showParticipantRole
                    onAddParticipant={handleAddParticipant}
                    onRemoveParticipant={handleRemoveParticipant}
                />
            </Form>
        </ConfirmModal>
    );
};

type Props = {
    open: boolean;
    onClose: (confirm: boolean, data?: {participant?: AnyParticipant}[]) => void;
};

export const InviteParticipantModal: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    const {
        settings: {
            featuresAvailability,
            userSession
        }
    } = useContext(UserContext);

    const {
        session, sessionParams, activeGuestParticipants, activeAssistantParticipants,
        canInviteGuest, canInviteAssistant
    } = useUserSession();

    const inviteParticipantsFn = useCallback((values: Values) =>
        agentActionsApi.inviteParticipants(sessionParams, values),
    [sessionParams]);

    const onSuccess = useCallback((data: {participant: AnyParticipant}[]) => {
        onClose(true, data);
    }, [onClose]);

    const {send: inviteParticipants, loading, error} =
        useApiData(inviteParticipantsFn, {onSuccess, notificationType: PredefinedNotifierType.Save});

    const {send: fetchUsers, data: users = []} = useApiData<UserSummary[]>(userApi.getUsersSummary);

    const guestEnabled = useMemo(() => featuresAvailability.guestEnabled && canInviteGuest,
        [featuresAvailability.guestEnabled, canInviteGuest]);
    const assistantEnabled = useMemo(() => featuresAvailability.assistantEnabled && canInviteAssistant,
        [featuresAvailability.assistantEnabled, canInviteAssistant]);

    const maxGuests = useMemo(
        () => guestEnabled ? Math.min(userSession.maxGuests - activeGuestParticipants.length, 1) : 0,
        [guestEnabled, userSession.maxGuests, activeGuestParticipants.length]
    );
    const maxAssistants = useMemo(
        () => assistantEnabled ? Math.min(userSession.maxAssistants - activeAssistantParticipants.length, 1) : 0,
        [assistantEnabled, userSession.maxAssistants, activeAssistantParticipants.length]
    );

    const initialValues: Values = useMemo(() => ({
        participants: []
    }), []);

    const submitForm = useCallback((values: Values) => {
        inviteParticipants(values);
    }, [inviteParticipants]);

    useEffect(() => {
        if (open && assistantEnabled) {
            fetchUsers();
        }
    }, [open, assistantEnabled, fetchUsers]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={submitForm}
        >
            {(props) => (
                <FormTemplate
                    title={t(T_KEYS.INVITE_MODAL.TITLE)}
                    {...props}
                    open={open}
                    assignment={session.assignment}
                    error={error}
                    loading={loading}
                    guestEnabled={guestEnabled}
                    assistantEnabled={assistantEnabled}
                    users={users}
                    maxParticipants={userSession.maxParticipants}
                    maxGuests={maxGuests}
                    maxAssistants={maxAssistants}
                    onClose={onClose}
                />
            )}
        </Formik>
    );
};